import {
    AfterViewInit,
    ChangeDetectionStrategy,
    ChangeDetectorRef,
    Component,
    ElementRef,
    HostListener,
    OnDestroy,
    effect,
    inject,
    input,
    output,
    viewChild,
} from '@angular/core';
import { FormsModule } from '@angular/forms';
import {
    Chart,
    ChartArea,
    ChartDataset,
    Decimation,
    Filler,
    Legend,
    LineElement,
    LinearScale,
    LinearScaleOptions,
    PointElement,
    ScatterController,
    ScatterDataPoint,
    Title,
    Tooltip,
} from 'chart.js';
import { range, sortedIndex, sum, uniq } from 'lodash-es';
import { Subscription } from 'rxjs';
import { arrayMax, arrayMin, colorToRgb } from '../../hgm-setup/src/module/util/core';
import { LOGGER } from '../../hgm-setup/src/module/util/logger';
import { UiOptions } from '../../hgm-setup/src/module/util/ui-options';
import { GroupSignal } from '../../providers/signal-group-provider';
import {
    RangeMap,
    SignalSettingsProvider,
    StyleMap,
    maxSignalDashLength,
    signalColors,
    signalDashes,
} from '../../providers/signal-settings-provider';
import { ThemeManager } from '../../providers/theme-manager';
import { xTickCallback } from '../../providers/util';
import { XAxisManager, XLimits } from '../../providers/x-axis-manager';
import { SignalNoData } from 'src/providers/data-provider';

interface Styles {
    backgroundColor: string;
    backgroundColorRgb: string;
    fontFamily: string;
    textColor: string;
    step600Color: string;
    textColorRgb: string;
    gridlineColor: string;
    zeroGridlineColor: string;
    cursorColor: string;
}

const mouseTouch = Symbol();

interface LegendSignal {
    name: string;
    color: string;
    dash: number[];
    yAxisID: string;
    unit: string;
    hidden?: boolean;
}

interface CursorPoint {
    signal: LegendSignal;
    pixelX: number;
    pixelY: number;
    labelValue: string;
}

const getBounds = (signals: GroupSignal[], ranges: RangeMap) => {
    let rawDataMin = arrayMin(signals.map((s) => s.minY).filter(isFinite));
    let rawDataMax = arrayMax(signals.map((s) => s.maxY).filter(isFinite));
    if (rawDataMin === undefined || !isFinite(rawDataMin)) rawDataMin = 0;
    if (rawDataMax === undefined || !isFinite(rawDataMax)) rawDataMax = 0;
    let dataMin = rawDataMin;
    let dataMax = rawDataMax;

    const userMin = arrayMin(
        signals
            .map((s) =>
                s.slot && typeof s.slot.engrToEngrFloat === 'function'
                    ? s.slot.engrToEngrFloat(ranges[s.key]?.minY)
                    : ranges[s.key]?.minY !== undefined
                      ? Number(ranges[s.key]?.minY)
                      : undefined,
            )
            .filter(isFinite) as number[],
    );
    const userMax = arrayMax(
        signals
            .map((s) =>
                s.slot && typeof s.slot.engrToEngrFloat === 'function'
                    ? s.slot.engrToEngrFloat(ranges[s.key]?.maxY)
                    : ranges[s.key]?.maxY !== undefined
                      ? Number(ranges[s.key]?.maxY)
                      : undefined,
            )
            .filter(isFinite) as number[],
    );

    const minFinal = Math.min(dataMin, userMin !== undefined ? userMin : dataMin);
    const maxFinal = Math.max(dataMax, userMax !== undefined ? userMax : dataMax);

    const span = isFinite(maxFinal) && isFinite(minFinal) ? maxFinal - minFinal : 0;
    let autoMin = (isFinite(minFinal) ? minFinal : 0) - span * 0.02;
    let autoMax = (isFinite(maxFinal) ? maxFinal : 0) + span * 0.02;
    if (span === 0) {
        autoMin = (isFinite(minFinal) ? minFinal : 0) - 1;
        autoMax = (isFinite(maxFinal) ? maxFinal : 0) + 1;
    }

    // if user explicit bound exists, use it - otherwise let chartJS autosuggest padding
    if (span === 0) {
        return {
            min: autoMin,
            max: autoMax,
            suggestedMin: autoMin,
            suggestedMax: autoMax,
        };
    }

    const slotMinRaw = arrayMin(signals.map((s) => s.slot?.engrMin).filter(isFinite));
    const slotMaxRaw = arrayMax(signals.map((s) => s.slot?.engrMax).filter(isFinite));

    // If we have no slot bounds, fallback to autoMin/Max
    const slotMin = slotMinRaw !== undefined ? slotMinRaw : autoMin;
    const slotMax = slotMaxRaw !== undefined ? slotMaxRaw : autoMax;

    // The chart limits should be based on the smaller of the signal limits during the data log and the engineering limits
    let defaultMin = Math.max(autoMin, slotMin);
    let defaultMax = Math.min(autoMax, slotMax);

    // Fallback just in case the intersection is invalid (e.g. data is entirely outside engr limits)
    if (defaultMin >= defaultMax) {
        defaultMin = autoMin;
        defaultMax = autoMax;
    }

    const bounds = {
        min: userMin !== undefined && isFinite(userMin) ? userMin : undefined,
        max: userMax !== undefined && isFinite(userMax) ? userMax : undefined,
        suggestedMin: userMin !== undefined && isFinite(userMin) ? userMin : defaultMin,
        suggestedMax: userMax !== undefined && isFinite(userMax) ? userMax : defaultMax,
    };

    // console.log(`[getBounds] Resolving Y Limits -> Min: ${bounds.min}, Max: ${bounds.max}, Span: ${span}`);

    return bounds;
};

Chart.register(LineElement, PointElement, ScatterController, LinearScale, Decimation, Filler, Legend, Title, Tooltip);

type LinearScaleFullOptions = Partial<LinearScaleOptions> & {
    type: 'linear';
};
interface LinearScales {
    [key: string]: LinearScaleFullOptions;
}
type MainDataset = ChartDataset<'scatter', ScatterDataPoint[]> & {
    unit: string;
};
type VfDataset = ChartDataset<'scatter', ScatterDataPoint[]>;

@Component({
    selector: 'signal-chart',
    templateUrl: 'signal-chart.component.html',
    changeDetection: ChangeDetectionStrategy.OnPush,
    imports: [FormsModule],
})
export class SignalChartComponent implements AfterViewInit, OnDestroy {
    signals = input<{ group: GroupSignal[]; styles: StyleMap; ranges: RangeMap } | undefined>();
    signalsNoData = input<{ group: SignalNoData[]; styles: StyleMap; ranges: RangeMap } | undefined>();
    id = input.required<number>();
    isUpdating = output<boolean>();

    legendSignals: LegendSignal[] = [];
    signalLabelBoxes: {
        left: string;
        right: string;
        top: string;
        bottom: string;
        onClicked: () => void;
    }[] = [];

    private subscriptions: Subscription[] = [];
    private chart: Chart | undefined;
    private chartCanvas = viewChild.required<ElementRef<HTMLCanvasElement>>('chartCanvas');
    private cursorCanvas = viewChild.required<ElementRef<HTMLCanvasElement>>('cursorCanvas');
    private resizeObserver: ResizeObserver | undefined;

    private dragChart = new Map<number | typeof mouseTouch, any>();
    private frameHandlerMousemove: MouseEvent | undefined;
    private frameHandlerChartTouchmoves = new Map<number, Touch>();
    private vfDatasets: ChartDataset<'scatter', ScatterDataPoint[]>[];
    private vfYAxes: LinearScales;
    private styles: Styles;
    private isChartUpdating = false;
    private isChartUpdateRequested = false;
    private yAxesUnits: Record<string, string> = {};
    private readonly logger = inject(LOGGER);
    private readonly cdr = inject(ChangeDetectorRef);
    private readonly themer = inject(ThemeManager);
    private readonly xAxisMgr = inject(XAxisManager);
    private readonly signalSettingsProvider = inject(SignalSettingsProvider);
    private readonly uiOptions = inject(UiOptions);
    private readonly minLeftPad = 4;
    private readonly minRightPad = this.uiOptions.isNarrowScreen() ? 20 : 10;

    constructor() {
        effect(() => {
            this.signals();
            this.signalsNoData();
            if (this.chart?.options.scales) {
                this.updateDatasets();
            }
        });
        effect(() => {
            this.themer.isDark();
            this.onDarkModeChange();
        });
    }

    isGroupSignal = (object: any): object is GroupSignal => 'slot' in object;

    ngOnInit() {
        this.subscriptions.push(
            this.xAxisMgr.userCoarseLimitsObs.subscribe({
                next: this.onCoarseLimitsUpdate,
            }),
        );
        this.subscriptions.push(
            this.xAxisMgr.userFineLimitsObs.subscribe({
                next: this.onFineLimitsUpdate,
            }),
        );
        this.subscriptions.push(
            this.xAxisMgr.cursorPosObs.subscribe({
                next: this.onCursorPosUpdate,
            }),
        );
        this.subscriptions.push(this.xAxisMgr.chartInsetObs.subscribe({ next: this.updateXInset }));
    }

    ngAfterViewInit() {
        this.getStyles();

        const { datasets, labels, scales, vfDatasets, vfYAxes, legendSignals } = this.makeDatasets();
        this.vfDatasets = vfDatasets;
        this.vfYAxes = vfYAxes;
        this.legendSignals = legendSignals;
        const chartContext = this.chartCanvas().nativeElement.getContext('2d');
        if (!chartContext) {
            this.logger.error('Could not get chart context');
            return;
        }
        this.chart = new Chart(chartContext, {
            type: 'scatter',
            data: {
                datasets,
                labels,
            },
            options: {
                animation: false,
                elements: {
                    line: { borderJoinStyle: 'round' },
                    point: { hitRadius: 100 },
                },
                layout: {
                    padding: this.makeChartPadding(),
                },
                maintainAspectRatio: false,
                responsive: true,
                scales,
                plugins: {
                    legend: {
                        display: false,
                        labels: {},
                    },
                    tooltip: {
                        enabled: false,
                    },
                },
            },
            plugins: [
                {
                    id: 'signalChartPlugin',
                    beforeUpdate: this.chartBeforeUpdate,
                    afterLayout: this.chartAfterLayout,
                    afterDraw: this.chartAfterDraw,
                },
            ],
        });
        this.updateXInset(); // make.*Padding can't do its job properly until chart exists
        this.xAxisMgr.setVf(this.id(), {
            datasets: this.vfDatasets,
            yAxes: this.vfYAxes,
        });

        this.resizeObserver = new ResizeObserver((entries) => {
            if (this.chart && entries.length > 0) {
                const rect = entries[0].contentRect;
                if (rect.width > 0 && rect.height > 0) {
                    // Re-evaluate scale correctly just in case
                    this.chartAfterLayout(this.chart);
                    this.redrawCursor(this.chart);
                }
            }
        });
        this.resizeObserver.observe(this.cursorCanvas().nativeElement);
    }

    ngOnDestroy() {
        if (this.resizeObserver) {
            this.resizeObserver.disconnect();
            this.resizeObserver = undefined;
        }
        this.subscriptions.map((sub) => sub.unsubscribe());
        this.subscriptions = [];
        this.xAxisMgr.clearChart(this.id());
        this.chart?.destroy();
        this.chartCanvas().nativeElement?.parentElement?.removeChild(this.chartCanvas().nativeElement);
        this.cursorCanvas().nativeElement?.parentElement?.removeChild(this.cursorCanvas().nativeElement);
    }

    getChartX(offsetX: number) {
        if (!this.chart) {
            return NaN;
        }
        const area = this.chart.chartArea;
        const limits = this.xAxisMgr.userFineLimits; // works because we set min/max instead of suggestedMin/Max
        return ((offsetX - area.left) * (limits[1] - limits[0])) / (area.right - area.left) + limits[0];
    }

    getOffsetX(chartX: number) {
        if (!this.chart) {
            return NaN;
        }
        const area = this.chart.chartArea;
        const limits = this.xAxisMgr.userFineLimits; // works because we set min/max instead of suggestedMin/Max
        return ((chartX - limits[0]) * (area.right - area.left)) / (limits[1] - limits[0]) + area.left;
    }

    @HostListener('window:mousemove', ['$event'])
    onMainMousemove(event: MouseEvent) {
        const prev = this.frameHandlerMousemove;
        this.frameHandlerMousemove = event;
        if (!prev) {
            requestAnimationFrame(this.onMainMousemoveFrame);
        }
    }

    onMainMousemoveFrame = () => {
        if (this.frameHandlerMousemove && this.chart?.canvas && this.dragChart.has(mouseTouch)) {
            const area = this.chart.chartArea;
            const xOffset = this.chart.canvas.getBoundingClientRect().x;
            this.xAxisMgr.cursorPos = this.getChartX(
                Math.min(Math.max(this.frameHandlerMousemove.clientX - xOffset, area.left), area.right),
            );
        }
        this.frameHandlerMousemove = undefined;
    };

    @HostListener('window:mouseup', [])
    onMainMouseup() {
        this.dragChart.delete(mouseTouch);
    }

    @HostListener('window:mouseleave', [])
    onMainMouseleave() {
        this.dragChart.delete(mouseTouch);
    }

    onChartMousedown(event: MouseEvent) {
        this.dragChart.set(mouseTouch, {});
        this.onMainMousemove(event);
    }

    onChartTouchstart(event: TouchEvent) {
        for (const touch of Array.from(event.changedTouches)) {
            if (this.dragChart.size) {
                break; // multitouch on the chart is right out, for now
            }
            this.dragChart.set(touch.identifier, {});
        }
        this.onChartTouchmove(event);
    }

    onChartTouchmove(event: TouchEvent) {
        const prev = this.frameHandlerChartTouchmoves.size;
        for (const touch of Array.from(event.changedTouches)) {
            this.frameHandlerChartTouchmoves.set(touch.identifier, touch);
        }
        if (!prev) {
            requestAnimationFrame(this.onChartTouchmoveFrame);
        }
    }

    onChartTouchmoveFrame = () => {
        if (!this.chart?.canvas) {
            this.logger.error('Could not get chart canvas');
            return;
        }
        const xOffset = this.chart.canvas.getBoundingClientRect().x;
        const area = this.chart.chartArea;
        for (const touch of this.frameHandlerChartTouchmoves.values()) {
            if (this.dragChart.has(touch.identifier)) {
                this.xAxisMgr.cursorPos = this.getChartX(Math.min(Math.max(touch.clientX - xOffset, area.left), area.right));
            }
        }
        this.frameHandlerChartTouchmoves.clear();
    };

    onChartTouchend(event: TouchEvent) {
        for (const touch of Array.from(event.changedTouches)) {
            this.dragChart.delete(touch.identifier);
        }
    }

    onWheel(event: WheelEvent) {
        if (!event.shiftKey && !event.ctrlKey) {
            return;
        }
        event.preventDefault();
        const limits = this.xAxisMgr.userFineLimits;
        const currentSpan = limits[1] - limits[0];

        if (event.ctrlKey) {
            let zoomFactor = Math.pow(1.002, event.deltaY);
            if (event.deltaMode === 1) zoomFactor = Math.pow(1.002, event.deltaY * 15);
            else if (event.deltaMode === 2) zoomFactor = Math.pow(1.002, event.deltaY * 100);

            const newSpan = currentSpan * zoomFactor;

            const chartInset = this.xAxisMgr.chartInset;
            const plotWidth = this.chartCanvas().nativeElement.clientWidth - chartInset.left - chartInset.right;

            let hoverFrac = 0.5;
            if (plotWidth > 0) {
                hoverFrac = (event.offsetX - chartInset.left) / plotWidth;
                hoverFrac = Math.max(0, Math.min(1, hoverFrac));
            }

            const hoverTime = limits[0] + hoverFrac * currentSpan;

            let newLeft = hoverTime - hoverFrac * newSpan;
            let newRight = hoverTime + (1 - hoverFrac) * newSpan;

            const coarseLimits = this.xAxisMgr.userCoarseLimits;
            if (newRight - newLeft > coarseLimits[1] - coarseLimits[0]) {
                newLeft = coarseLimits[0];
                newRight = coarseLimits[1];
            } else {
                if (newLeft < coarseLimits[0]) {
                    newRight += coarseLimits[0] - newLeft;
                    newLeft = coarseLimits[0];
                }
                if (newRight > coarseLimits[1]) {
                    newLeft -= newRight - coarseLimits[1];
                    newRight = coarseLimits[1];
                }
            }

            this.xAxisMgr.userFineLimits = [newLeft, newRight];
        } else if (event.shiftKey) {
            let delta = event.deltaX;
            if (Math.abs(event.deltaY) > Math.abs(event.deltaX)) {
                delta = event.deltaY;
            }

            let pixels = delta;
            if (event.deltaMode === 1) {
                pixels *= 15;
            } else if (event.deltaMode === 2) {
                pixels *= 100;
            }

            if (!this.chart?.canvas?.clientWidth) {
                return;
            }

            const width = this.chart.canvas.clientWidth;
            const timeDelta = (pixels / width) * currentSpan;

            let newLeft = limits[0] + timeDelta;
            let newRight = limits[1] + timeDelta;

            const coarseLimits = this.xAxisMgr.userCoarseLimits;
            if (newLeft < coarseLimits[0]) {
                newLeft = coarseLimits[0];
                newRight = newLeft + currentSpan;
            }
            if (newRight > coarseLimits[1]) {
                newRight = coarseLimits[1];
                newLeft = newRight - currentSpan;
            }

            if (newLeft < coarseLimits[0]) {
                newLeft = coarseLimits[0];
                newRight = coarseLimits[1];
            }

            this.xAxisMgr.userFineLimits = [newLeft, newRight];
        }
    }

    onSignalToggled(signal: LegendSignal) {
        if (!this.chart?.data?.datasets) {
            this.logger.error('Could not get chart datasets');
            return;
        }
        const index = this.legendSignals.indexOf(signal);
        if (index < 0 || index >= this.chart.data.datasets.length) {
            this.logger.warn('Signal toggled but not found in list', index, signal);
            return;
        }
        signal.hidden = !signal.hidden;
        this.chart.data.datasets[index].hidden = signal.hidden;
        this.vfDatasets[index].hidden = signal.hidden;
        this.updateChart();
        this.xAxisMgr.setVf(this.id(), {
            datasets: this.vfDatasets,
            yAxes: this.vfYAxes,
        });
    }

    onNoDataSignalToggled(signal: LegendSignal) {
        signal.hidden = !signal.hidden;
        this.updateChart();
    }

    private updateDatasets = () => {
        if (this.chart?.options.scales) {
            const { datasets, labels, scales, vfDatasets, vfYAxes, legendSignals } = this.makeDatasets();
            this.vfDatasets = vfDatasets;
            this.vfYAxes = vfYAxes;
            this.legendSignals = legendSignals;
            this.chart.data.datasets = datasets;
            this.chart.data.labels = labels;
            this.chart.options.scales = scales;
            this.cdr.markForCheck();
            this.updateChart();
            this.xAxisMgr.setVf(this.id(), {
                datasets: this.vfDatasets,
                yAxes: this.vfYAxes,
            });
        }
    };

    private updateXInset = () => {
        if (this.chart?.options.layout) {
            this.chart.options.layout.padding = this.makeChartPadding();
            this.updateChart();
        }
    };

    private onCoarseLimitsUpdate = () => {
        this.updateDatasets();
    };

    private onFineLimitsUpdate = (limits: XLimits) => {
        if (this.chart?.options.scales?.x) {
            this.chart.options.scales.x.min = limits[0];
            this.chart.options.scales.x.max = limits[1];
            const { datasets, labels, scales } = this.makeDatasets();
            this.chart.data.datasets = datasets;
            this.chart.data.labels = labels;
            this.chart.options.scales = scales;
            this.updateChart();
        }
    };

    private onCursorPosUpdate = () => {
        if (this.chart) {
            this.redrawCursor(this.chart);
        }
    };

    private makeChartPadding(): Partial<ChartArea> {
        const top = 0;
        const bottom = 12;
        if (!this.chart?.options.layout?.padding || !this.chart.canvas) {
            return {
                top,
                right: this.minRightPad,
                bottom,
                left: this.minLeftPad,
            };
        }

        const desired = this.xAxisMgr.chartInset;
        const padding = this.chart.options.layout.padding as ChartArea;
        return {
            top,
            right: Math.max(this.minRightPad, desired.right - (this.chart.canvas.width - this.chart.chartArea.right - padding.right!)),
            bottom,
            left: Math.max(this.minLeftPad, desired.left - (this.chart.chartArea.left - padding.left!)),
        };
    }

    private makeDatasets(): {
        datasets: ChartDataset<'scatter', ScatterDataPoint[]>[];
        labels: string[];
        scales: LinearScales;
        vfDatasets: ChartDataset<'scatter', ScatterDataPoint[]>[];
        vfYAxes: LinearScales;
        legendSignals: LegendSignal[];
    } {
        if (!this.signals()) {
            return {
                datasets: [],
                labels: [],
                scales: {},
                vfDatasets: [],
                vfYAxes: {},
                legendSignals: [],
            };
        }
        const { group, styles, ranges } = this.signals()!;
        const slots = uniq(group.map((s) => s.slot));

        const signalYAxes: {
            unit: string;
            slot: any;
            signals: GroupSignal[];
            dedicated: boolean | null;
        }[] = slots.map((slot) => {
            const signals = group.filter((s) => s.slot === slot);
            const unit = slot?.unit || (signals.length > 0 ? (signals[0] as any).unit || '' : '');
            return {
                unit,
                slot,
                signals,
                dedicated: null,
            };
        });
        for (let i = signalYAxes.length - 1; i >= 0; --i) {
            const existing = signalYAxes[i];
            const nonDedicated = existing.signals.filter((s) => !ranges[s.key]?.dedicatedY);
            const dedicated = existing.signals.filter((s) => ranges[s.key]?.dedicatedY);
            const split = [
                {
                    unit: existing.unit,
                    slot: existing.slot,
                    signals: nonDedicated,
                    dedicated: false,
                },
                ...dedicated.map((s) => ({
                    unit: existing.unit,
                    slot: existing.slot,
                    signals: [s],
                    dedicated: true,
                })),
            ];
            signalYAxes.splice(i, 1, ...split);
        }
        const scales: LinearScales = {
            x: {
                position: 'bottom',
                grid: {
                    color: (ctx: any) => (ctx.tick.value === 0 ? this.styles.zeroGridlineColor : this.styles.gridlineColor),
                },
                min: this.xAxisMgr.userFineLimits[0],
                max: this.xAxisMgr.userFineLimits[1],
                title: {
                    display: true,
                    font: {
                        family: this.styles.fontFamily,
                    },
                    color: this.styles.step600Color,
                    padding: { top: -4, bottom: 0 },
                },
                ticks: {
                    callback: xTickCallback,
                    color: this.styles.step600Color,
                },
                type: 'linear',
            } as unknown as LinearScaleFullOptions,
        };
        this.yAxesUnits = {};
        for (const [i, signalYAxis] of signalYAxes.entries()) {
            this.yAxesUnits[`y-axis-${i}`] = signalYAxis.unit;
            const bounds = getBounds(signalYAxis.signals, ranges);
            const rawPrecision = arrayMax(signalYAxis.signals.map((s) => (s && s.slot ? s.slot.precision : 2)));
            const precision = isFinite(rawPrecision) && rawPrecision >= 0 && rawPrecision <= 100 ? Math.floor(rawPrecision) : 2;
            const scaleConfig = {
                position: 'left',
                type: 'linear',
                display: 'auto',
                suggestedMin: bounds.suggestedMin,
                suggestedMax: bounds.suggestedMax,
                ticks: {
                    callback: (value: any) => {
                        const slot = signalYAxis.slot;
                        if (slot && 'encodingStringList' in slot) {
                            const numericValue = Number(value);
                            // only return strings for exact matches, let ChartJS handle interpolations if any
                            if (Number.isInteger(numericValue)) {
                                const engrString = slot.toEngrString(numericValue);
                                if (engrString !== '' && engrString !== undefined) {
                                    return engrString;
                                }
                            }
                        }
                        return Number(value).toFixed(precision);
                    },
                    stepSize:
                        signalYAxis.slot &&
                        'encodingStringList' in signalYAxis.slot &&
                        bounds.suggestedMax !== undefined &&
                        bounds.suggestedMin !== undefined &&
                        bounds.suggestedMax - bounds.suggestedMin <= 50
                            ? 1
                            : undefined,
                    precision,
                    color: signalColors[
                        signalYAxis.signals[0] && styles[signalYAxis.signals[0].key] ? styles[signalYAxis.signals[0].key].color : 0
                    ],
                    textStrokeColor: this.styles.step600Color,
                },
                grid: {
                    color: (ctx: any) => (ctx.tick.value === 0 ? this.styles.zeroGridlineColor : this.styles.gridlineColor),
                    tickLength: 5,
                },
                title: {
                    color: this.styles.step600Color,
                    display: signalYAxis.dedicated || false,
                    font: {
                        family: this.styles.fontFamily,
                    },
                    padding: 0,
                    text: signalYAxis.signals.length > 0 ? signalYAxis.signals[0].name : '',
                },
            } as any;
            if (Number.isFinite(bounds.min)) scaleConfig.min = bounds.min;
            if (Number.isFinite(bounds.max)) scaleConfig.max = bounds.max;
            if (Number.isFinite(bounds.suggestedMin)) scaleConfig.suggestedMin = bounds.suggestedMin;
            if (Number.isFinite(bounds.suggestedMax)) scaleConfig.suggestedMax = bounds.suggestedMax;
            scales[`y-axis-${i}`] = scaleConfig;
        }

        const mixedGroup: (GroupSignal | SignalNoData)[] = [...group];
        if (this.signalsNoData()) {
            for (const signalGroupNoData of this.signalsNoData()!.group) {
                if (signalGroupNoData) {
                    mixedGroup.push(signalGroupNoData);
                }
            }
        }
        const stylesFromSignalSettings = this.signalSettingsProvider.styles;
        const labels = mixedGroup.map((s) => s.name);
        let style;
        const datasets = Array.from(mixedGroup.entries()).map(([i, signal]): MainDataset => {
            const isGroupSignal = this.isGroupSignal(signal);
            style = styles[signal.key];
            let yAxisIndex = 0;
            let unit;
            let data;
            if (isGroupSignal) {
                yAxisIndex = signalYAxes.findIndex((a) => a.signals.includes(signal));
                unit = signalYAxes.find((a) => a.signals.includes(signal))?.unit || '';
                const xCoords = signal.data.map((d) => d[0]);
                const beginIndex = Math.max(sortedIndex(xCoords, this.xAxisMgr.userFineLimits[0]) - 1, 0);
                const endIndex = Math.min(sortedIndex(xCoords, this.xAxisMgr.userFineLimits[1]) + 1, xCoords.length);
                data = signal.data.slice(beginIndex, endIndex).map((d) => ({ x: d[0], y: isFinite(d[1]) ? d[1] : null }));
            } else {
                yAxisIndex = 0;
                unit = '';
                data = [];
                style = stylesFromSignalSettings ? stylesFromSignalSettings[signal.key] : undefined;
            }
            if (!style) {
                style = { color: 0, dash: 0 };
            }
            return {
                yAxisID: `y-axis-${yAxisIndex}`,
                borderColor: signalColors[style.color],
                pointBackgroundColor: signalColors[style.color],
                borderDash: signalDashes[style.dash],
                borderWidth: 1.5,
                pointBorderWidth: 0,
                pointHoverBorderWidth: 2,
                pointRadius: 0,
                pointHoverRadius: 2,
                data,
                fill: false,
                label: mixedGroup[i].name,
                showLine: true,
                unit,
                hidden: this.legendSignals[i]?.hidden,
                parsing: false,
                normalized: true,
                clip: 0,
            };
        });

        const vfYAxes: LinearScales = {};
        for (const i of signalYAxes.keys()) {
            const vfScaleConfig = {
                display: false,
                position: 'left',
                type: 'linear',
                ticks: {
                    display: false,
                },
                suggestedMin: Number.isFinite((scales[`y-axis-${i}`] as any).suggestedMin)
                    ? (scales[`y-axis-${i}`] as any).suggestedMin
                    : undefined,
                suggestedMax: Number.isFinite((scales[`y-axis-${i}`] as any).suggestedMax)
                    ? (scales[`y-axis-${i}`] as any).suggestedMax
                    : undefined,
                grid: { display: false },
                title: { display: false },
            } as any;
            if (Number.isFinite((scales[`y-axis-${i}`] as any).min)) vfScaleConfig.min = (scales[`y-axis-${i}`] as any).min;
            if (Number.isFinite((scales[`y-axis-${i}`] as any).max)) vfScaleConfig.max = (scales[`y-axis-${i}`] as any).max;
            vfYAxes[`y-axis-${this.id()}-${i}`] = vfScaleConfig;
        }

        const vfDatasets = Array.from(group.entries()).map(([i, signal]): VfDataset => {
            const yAxisIndex = signalYAxes.findIndex((a) => a.signals.includes(group[i]));
            const xCoords = group[i].data.map((d) => d[0]);
            const beginIndex = sortedIndex(xCoords, this.xAxisMgr.userCoarseLimits[0]);
            const endIndex = sortedIndex(xCoords, this.xAxisMgr.userCoarseLimits[1]);
            const data = group[i].data.slice(beginIndex, endIndex).map((d) => ({ x: d[0], y: isFinite(d[1]) ? d[1] : null }));
            let style = styles[signal.key];
            if (!style) {
                style = { color: 0, dash: 0 };
            }
            return {
                yAxisID: `y-axis-${this.id()}-${yAxisIndex}`,
                borderColor: signalColors[style.color],
                pointBackgroundColor: signalColors[style.color],
                borderDash: signalDashes[style.dash],
                borderWidth: 1.5,
                pointBorderWidth: 0,
                pointHoverBorderWidth: 0,
                pointRadius: 0,
                pointHoverRadius: 0,
                data,
                fill: false,
                showLine: true,
                hidden: this.legendSignals[i]?.hidden,
                parsing: false,
                normalized: true,
                clip: 0,
            };
        });

        const legendSignals = Array.from(datasets.entries()).map(([i, ds]): LegendSignal => ({
            name: ds.label || '',
            color: ds.borderColor as string,
            dash: (ds.borderDash as number[]) || [],
            yAxisID: ds.yAxisID || '',
            unit: ds.unit,
            hidden: this.legendSignals[i]?.hidden,
        }));

        return { datasets, labels, scales, vfDatasets, vfYAxes, legendSignals };
    }

    private getStyles() {
        const style = getComputedStyle(document.getElementsByTagName('app-root')[0]);
        const textColorRgb = style.getPropertyValue('--ion-text-color-rgb').replace(/ /g, '');
        this.styles = {
            backgroundColor: style.getPropertyValue('--ion-background-color').replace(/ /g, ''),
            backgroundColorRgb: style.getPropertyValue('--ion-background-color-rgb').replace(/ /g, ''),
            fontFamily: style.getPropertyValue('--ion-font-family').replace(/^ /, '').replace(/ $/, ''),
            textColor: style.getPropertyValue('--ion-text-color').replace(/ /g, ''),
            step600Color: style.getPropertyValue('--ion-color-step-600').replace(/ /g, ''),
            textColorRgb,
            gridlineColor: `rgba(${textColorRgb},0.1)`,
            zeroGridlineColor: `rgba(${textColorRgb},0.25)`,
            cursorColor: style.getPropertyValue('--ion-text-color').replace(/ /g, ''),
        };
    }

    private chartBeforeUpdate = () => {
        this.isChartUpdating = true;
        this.isUpdating.emit(true);
    };

    private chartAfterLayout = (chart: Chart) => {
        if (!chart.options.layout?.padding || !chart.canvas) {
            this.logger.error('Could not get chart options or canvas');
            return;
        }
        const padding = chart.options.layout.padding as ChartArea;
        this.xAxisMgr.setMinInset(this.id(), {
            left: chart.chartArea.left - padding.left! + this.minLeftPad,
            right: chart.canvas.width - chart.chartArea.right - padding.right! + this.minRightPad,
        });
        this.cursorCanvas().nativeElement.width = chart.canvas.width;
        this.cursorCanvas().nativeElement.height = chart.canvas.height;
        const cursorCanvasContext = this.cursorCanvas().nativeElement.getContext('2d');
        if (!cursorCanvasContext) {
            this.logger.error('Could not get cursor canvas');
            return;
        }

        const rect = chart.canvas.getBoundingClientRect();
        if (rect.width === 0 || rect.height === 0) {
            return;
        }

        cursorCanvasContext.scale(chart.canvas.width / rect.width, chart.canvas.height / rect.height);
    };

    private chartAfterDraw = (chart: Chart) => {
        const ctx = chart.ctx;
        ctx.save();
        ctx.fillStyle = this.styles.step600Color;
        const fontSize = 11;
        ctx.font = `bold ${fontSize}px ${this.styles.fontFamily}`;
        ctx.textAlign = 'center';
        ctx.textBaseline = 'top';

        // Collect all active scales sorted by their left coordinate (outermost to innermost)
        const activeScales = Object.keys(this.yAxesUnits)
            .map((id) => chart.scales[id])
            .filter((scale) => scale !== undefined)
            .sort((a, b) => a.left - b.left);

        // Track which 'left' coordinates we've already drawn units/lines for
        // (in 'Single Chart' mode, multiple axes share the exact same coordinates)
        const drawnLefts = new Set<number>();

        for (let i = 0; i < activeScales.length; i++) {
            const scale = activeScales[i];

            // Skip drawing duplicate columns if they are stacked exactly on top of each other
            const roundedLeft = Math.round(scale.left);
            if (drawnLefts.has(roundedLeft)) {
                continue;
            }
            drawnLefts.add(roundedLeft);

            // Determine the right boundary of the column specifically for THIS axis.
            // It spans from its own left edge to the left edge of the NEXT inner scale
            // (or to the main chart area if it's the innermost axis).
            let rightBoundary = chart.chartArea.left;
            for (let j = i + 1; j < activeScales.length; j++) {
                if (Math.round(activeScales[j].left) > roundedLeft) {
                    rightBoundary = activeScales[j].left;
                    break;
                }
            }

            // Draw bottom unit label
            if (this.yAxesUnits[scale.id]) {
                const text = this.yAxesUnits[scale.id];
                // Center the unit precisely in the middle of this column's boundaries
                const x = (scale.left + rightBoundary) / 2;
                const y = chart.chartArea.bottom + 12;
                ctx.fillText(text, x, y);
            }

            // Draw right vertical divider if it is not the main chart area wall
            if (rightBoundary < chart.chartArea.left - 5) {
                ctx.beginPath();
                ctx.strokeStyle = this.styles.gridlineColor;
                ctx.lineWidth = 1;
                // Leave a little margin to the right of the numbers, span down past X-axis
                const lineX = rightBoundary - 5;
                ctx.moveTo(lineX, scale.top);
                ctx.lineTo(lineX, chart.chartArea.bottom + 25);
                ctx.stroke();
            }
        }
        ctx.restore();

        this.redrawCursor(chart);
        if (this.isChartUpdateRequested) {
            this.isChartUpdateRequested = false;
            chart.update();
        } else {
            this.isChartUpdating = false;
            this.isUpdating.emit(false);
        }
    };

    private redrawCursor(chart: Chart) {
        if (this.xAxisMgr.cursorPos === undefined) {
            return;
        }
        let cursorX = this.getOffsetX(this.xAxisMgr.cursorPos);
        if (cursorX === undefined || !isFinite(cursorX)) {
            return;
        }
        const area = chart.chartArea;
        cursorX = Math.min(Math.max(cursorX, area.left), area.right);
        const canvas = this.cursorCanvas().nativeElement;
        const ctx = canvas.getContext('2d');
        if (!ctx) {
            console.error('Could not get cursor canvas context');
            return;
        }
        ctx.clearRect(0, 0, canvas.width, canvas.height);
        ctx.strokeStyle = this.styles.cursorColor;
        ctx.lineWidth = 2;
        ctx.beginPath();
        ctx.moveTo(cursorX, area.top - 5);
        ctx.lineTo(cursorX, area.bottom + 5);
        ctx.stroke();

        // find the most recent point for each signal (or the next if no previous)
        const points = this.getCursorPoints(chart, this.xAxisMgr.cursorPos);

        if (!points.length && !this.signalsNoData) {
            return;
        }

        // draw a circle around each point
        ctx.strokeStyle = this.styles.cursorColor;
        ctx.lineWidth = 2;
        for (const point of points.filter((p) => !p.signal.hidden)) {
            ctx.beginPath();
            ctx.arc(point.pixelX, point.pixelY, 3, 0, Math.PI * 2);
            ctx.stroke();
        }

        // figure out where to draw the label: set up the font/size, figure out width, then see which side it fits on
        const fontSize = 12;
        const font = `${fontSize}px ${this.styles.fontFamily}`;
        ctx.font = font;
        const outerPadding = 6;
        const innerHPadding = 6;
        const innerHColPadding = 12;
        const innerVPadding = 4;
        const margin = 5;
        const lineSampleRatio = 2 / 3;
        const lineSampleLength = maxSignalDashLength * lineSampleRatio;
        const headerPadding = 18;
        const borderRadius = 5;
        const cursorLabelClearance = 6;
        const chartHeight = chart.chartArea.bottom - chart.chartArea.top;
        const maxRows = Math.max(
            1,
            Math.floor(
                (chartHeight - outerPadding - headerPadding - fontSize + innerVPadding - outerPadding - 2 * cursorLabelClearance) /
                    (fontSize + innerVPadding),
            ),
        );
        const nColumns = Math.ceil((points.length + (this.signalsNoData() ? this.signalsNoData()!.group?.length : 0)) / maxRows);
        const nRows = Math.ceil((points.length + (this.signalsNoData() ? this.signalsNoData()!.group?.length : 0)) / nColumns);
        const pointColumns = range(nColumns).map((i) => {
            const colPoints = points.slice(i * nRows, i * nRows + nRows);
            const maxNameWidth = arrayMax(
                colPoints
                    .map((p) => ctx.measureText(p.signal.name).width)
                    .concat(this.signalsNoData() ? this.signalsNoData()!.group.map((p) => ctx.measureText(p.name).width) : []),
            );
            ctx.font = `bold ${font}`;
            const maxValueWidth = arrayMax(colPoints.map((p) => ctx.measureText(p.labelValue).width).concat([0]));
            ctx.font = font;
            return { points: colPoints, maxNameWidth, maxValueWidth };
        });

        const columnsWidth = sum(
            pointColumns.map((c) => c.maxNameWidth + innerHPadding + lineSampleLength + innerHPadding + c.maxValueWidth + innerHColPadding),
        );
        const totalWidth = outerPadding + columnsWidth - innerHColPadding + outerPadding;
        const totalHeight = outerPadding + headerPadding + fontSize + nRows * (fontSize + innerVPadding) - innerVPadding + outerPadding;

        const canvasRect = canvas.getBoundingClientRect();
        const labelLeft = cursorX + totalWidth + margin * 2 < canvasRect.width ? cursorX + margin : cursorX - totalWidth - margin;
        const labelRight = labelLeft + totalWidth;
        const middleY = (area.top + area.bottom) / 2;
        const labelTop = middleY - totalHeight / 2;
        const labelBottom = middleY + totalHeight / 2;

        // draw the label: signal names + colors, values, round rect border
        ctx.strokeStyle = this.styles.cursorColor;
        ctx.lineWidth = 1;
        ctx.fillStyle = `rgba(${this.styles.backgroundColorRgb}, 0.8)`;
        ctx.beginPath();
        ctx.moveTo(labelLeft, labelTop + borderRadius);
        ctx.arcTo(labelLeft, labelTop, labelLeft + borderRadius, labelTop, borderRadius);
        ctx.lineTo(labelRight - borderRadius, labelTop);
        ctx.arcTo(labelRight, labelTop, labelRight, labelTop + borderRadius, borderRadius);
        ctx.lineTo(labelRight, labelBottom - borderRadius);
        ctx.arcTo(labelRight, labelBottom, labelRight - borderRadius, labelBottom, borderRadius);
        ctx.lineTo(labelLeft + borderRadius, labelBottom);
        ctx.arcTo(labelLeft, labelBottom, labelLeft, labelBottom - borderRadius, borderRadius);
        ctx.lineTo(labelLeft, labelTop + borderRadius);
        ctx.fill();
        ctx.stroke();

        let baselineX = labelLeft + outerPadding;
        this.signalLabelBoxes = [];

        for (const column of pointColumns) {
            const lineSampleX = baselineX + column.maxNameWidth + innerHPadding;
            const valueX = lineSampleX + lineSampleLength + innerHPadding;
            if (column !== pointColumns[0]) {
                ctx.strokeStyle = this.styles.zeroGridlineColor;
                ctx.lineWidth = 1;
                const dividerX = Math.round(baselineX - innerHColPadding / 2 + 0.5) - 0.5;
                ctx.beginPath();
                ctx.moveTo(dividerX, labelTop + outerPadding + fontSize + headerPadding / 2);
                ctx.lineTo(dividerX, labelBottom);
                ctx.stroke();
            }
            let baselineY = labelTop + outerPadding + headerPadding + fontSize;
            ctx.textBaseline = 'bottom';
            const columnWidth = column.maxNameWidth + innerHPadding + lineSampleLength + innerHPadding + column.maxValueWidth;
            for (const point of column.points) {
                this.signalLabelBoxes.push({
                    top: `${baselineY - fontSize - innerVPadding / 2}px`,
                    bottom: `${canvasRect.height - (baselineY + innerVPadding / 2)}px`,
                    left: `${baselineX - innerHColPadding / 2}px`,
                    right: `${canvasRect.width - (baselineX + columnWidth + innerHColPadding / 2)}px`,
                    onClicked: () => this.onSignalToggled(point.signal),
                });

                ctx.textAlign = 'right';
                ctx.fillStyle = point.signal.hidden ? `rgba(${this.styles.textColorRgb},0.5)` : this.styles.textColor;
                ctx.fillText(point.signal.name, baselineX + column.maxNameWidth, baselineY);

                ctx.textAlign = 'left';
                ctx.font = `bold ${font}`;
                ctx.fillText(point.labelValue, valueX, baselineY);
                ctx.font = font;

                const lineSampleY = Math.round(baselineY - fontSize / 2);
                ctx.strokeStyle = point.signal.hidden ? `rgba(${colorToRgb(point.signal.color)},0.5)` : point.signal.color;
                ctx.lineWidth = 1.5;
                ctx.setLineDash(point.signal.dash.map((v) => v * lineSampleRatio));
                ctx.beginPath();
                ctx.moveTo(lineSampleX, lineSampleY);
                ctx.lineTo(lineSampleX + lineSampleLength, lineSampleY);
                ctx.stroke();
                ctx.setLineDash([]);
                baselineY += fontSize + innerVPadding;
            }

            if (this.signalsNoData()?.group.length) {
                const noDataSignals = this.signalsNoData()!.group;
                let i = column.points.length;
                noDataSignals.forEach(() => {
                    const legendSignal = this.legendSignals[i];
                    this.signalLabelBoxes.push({
                        top: `${baselineY - fontSize - innerVPadding / 2}px`,
                        bottom: `${canvasRect.height - (baselineY + innerVPadding / 2)}px`,
                        left: `${baselineX - innerHColPadding / 2}px`,
                        right: `${canvasRect.width - (baselineX + columnWidth + innerHColPadding / 2)}px`,
                        onClicked: () => this.onNoDataSignalToggled(legendSignal),
                    });
                    ctx.textAlign = 'right';
                    ctx.fillStyle = legendSignal.hidden ? `rgba(${this.styles.textColorRgb},0.5)` : this.styles.textColor;
                    ctx.fillText(this.legendSignals[i].name, baselineX + column.maxNameWidth, baselineY);

                    const lineSampleY = Math.round(baselineY - fontSize / 2);
                    ctx.strokeStyle = legendSignal.hidden
                        ? `rgba(${colorToRgb(this.legendSignals[i].color)},0.5)`
                        : this.legendSignals[i].color;
                    ctx.lineWidth = 1.5;
                    ctx.setLineDash(this.legendSignals[i].dash.map((v) => v * lineSampleRatio));
                    ctx.beginPath();
                    ctx.moveTo(lineSampleX, lineSampleY);
                    ctx.lineTo(lineSampleX + lineSampleLength, lineSampleY);
                    ctx.stroke();
                    ctx.setLineDash([]);
                    baselineY += fontSize + innerVPadding;
                    i++;
                });
            }
            baselineX += columnWidth + innerHColPadding;
        }

        ctx.textAlign = 'center';
        ctx.fillStyle = this.styles.textColor;
        ctx.font = `bold ${ctx.font}`;
        let cursorPosStr = (this.xAxisMgr.cursorPos / 1000).toFixed(3);
        if (!cursorPosStr.startsWith('-')) {
            cursorPosStr = '+' + cursorPosStr;
        }
        ctx.fillText(`${cursorPosStr} sec`, (labelLeft + labelRight) / 2, labelTop + outerPadding + fontSize);
    }

    private getCursorPoints(chart: Chart, cursorPos: number): CursorPoint[] {
        const userLimits = this.xAxisMgr.userFineLimits;
        const area = chart.chartArea;
        const points: CursorPoint[] = [];
        const group = this.signals()?.group || [];
        for (const [iSignal, signal] of group.entries()) {
            const legendSignal = this.legendSignals[iSignal];
            const onePast = signal.data.findIndex((v) => v[0] > cursorPos);
            let iPoint = onePast - 1;
            if (onePast === -1) {
                // cursor is to the right of all points
                iPoint = signal.data.length - 1;
            } else if (onePast === 0) {
                // cursor is to the left of all points
                iPoint = 0;
            }
            const point = signal.data[iPoint];
            const x = area.left + ((point[0] - userLimits[0]) * (area.right - area.left)) / (userLimits[1] - userLimits[0]);
            const y = (chart as any).scales[legendSignal.yAxisID].getPixelForValue(point[1], iPoint, iSignal);
            const engrString =
                signal.slot && typeof signal.slot.engrToEngrString === 'function'
                    ? signal.slot.engrToEngrString(point[1])
                    : point[1].toFixed(signal.slot?.precision ?? 2);
            let labelValue = '?';
            if (engrString) {
                if (signal.unit) {
                    labelValue = `${engrString} ${signal.unit}`;
                } else {
                    labelValue = engrString;
                }
            }
            points.push({
                signal: legendSignal,
                pixelX: x,
                pixelY: y,
                labelValue,
            });
        }
        return points;
    }

    private onDarkModeChange = () => {
        this.getStyles();
        this.updateChart();
    };

    private updateChart() {
        if (this.isChartUpdating) {
            this.isChartUpdateRequested = true;
        } else {
            this.chart?.update();
        }
    }
}
